import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { userApi, AdminUser } from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import AppliedFilters from '../../components/UI/AppliedFilters';
import { canManageUsers } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import { IDENTITY_PROVIDERS } from '../../config/identityProviders';
import { confirmAction } from '../../utils/confirmAction';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';

const hasCourseOwner = (roles: string[]) => roles.some((role) => role.toLowerCase() === 'courseowner' || role.toLowerCase() === 'coursecreator');

const identityLabel = (item: AdminUser) => {
  if (item.identityProviderId === IDENTITY_PROVIDERS.Google || item.identityProvider === 'Google') return 'Google';
  if (item.identityProviderId === IDENTITY_PROVIDERS.LinkedIn || item.identityProvider === 'LinkedIn') return 'LinkedIn';
  if (item.identityProvider && item.identityProvider !== 'Local' && item.identityProvider !== 'Email') {
    return item.identityProvider;
  }
  return 'Email / password';
};

const UsersAdminPage: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', username: '', mobile: '', password: '', roles: ['User'] as string[], courseOwnerRevenuePercent: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [edit, setEdit] = useState({ username: '', mobile: '', roles: ['User'] as string[], isActive: true, isEmailVerified: true, password: '', courseOwnerRevenuePercent: '' });
  const [search, setSearch] = useState('');
  const [roleFilters, setRoleFilters] = useState<string[]>([]);
  const [includeNoRole, setIncludeNoRole] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');

  const load = () => {
    userApi.adminListUsers()
      .then((r) => {
        setUsers(r.data.users || []);
        setRoles(r.data.roles || []);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not load users.'));
  };

  useEffect(() => { load(); }, []);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    const roleFilterActive = roleFilters.length > 0 || includeNoRole;
    return users.filter((item) => {
      const itemRoles = item.roles?.length ? item.roles : (item.role ? [item.role] : []);
      const matchesSearch = !query
        || item.email?.toLowerCase().includes(query)
        || item.username?.toLowerCase().includes(query)
        || item.mobile?.toLowerCase().includes(query);
      const matchesRole = !roleFilterActive
        || (includeNoRole && itemRoles.length === 0)
        || roleFilters.some((role) => itemRoles.includes(role));
      const matchesActive = activeFilter === 'all'
        || (activeFilter === 'active' && item.isActive)
        || (activeFilter === 'inactive' && !item.isActive);
      const matchesVerified = verifiedFilter === 'all'
        || (verifiedFilter === 'verified' && item.isEmailVerified)
        || (verifiedFilter === 'unverified' && !item.isEmailVerified);
      const matchesProvider = providerFilter === 'all' || identityLabel(item) === providerFilter;
      return matchesSearch && matchesRole && matchesActive && matchesVerified && matchesProvider;
    });
  }, [users, search, roleFilters, includeNoRole, activeFilter, verifiedFilter, providerFilter]);

  const clearFilters = () => {
    setSearch('');
    setRoleFilters([]);
    setIncludeNoRole(false);
    setActiveFilter('all');
    setVerifiedFilter('all');
    setProviderFilter('all');
  };

  const toggleRoleFilter = (role: string) => {
    setRoleFilters((current) =>
      current.includes(role) ? current.filter((item) => item !== role) : [...current, role]
    );
  };

  const appliedFilters = [
    ...(search.trim() ? [{ key: 'search', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') }] : []),
    ...roleFilters.map((role) => ({
      key: `role-${role}`,
      label: `Role: ${role}`,
      onRemove: () => setRoleFilters((current) => current.filter((item) => item !== role)),
    })),
    ...(includeNoRole ? [{ key: 'no-role', label: 'Role: No role', onRemove: () => setIncludeNoRole(false) }] : []),
    ...(activeFilter !== 'all' ? [{ key: 'active', label: `Active: ${activeFilter}`, onRemove: () => setActiveFilter('all') }] : []),
    ...(verifiedFilter !== 'all' ? [{ key: 'verified', label: `Verified: ${verifiedFilter}`, onRemove: () => setVerifiedFilter('all') }] : []),
    ...(providerFilter !== 'all' ? [{ key: 'provider', label: `Identity: ${providerFilter}`, onRemove: () => setProviderFilter('all') }] : []),
  ];

  if (!canManageUsers(user)) {
    return <p className="text-gray-600">Only the Owner or User Admin can manage users, profiles, and roles.</p>;
  }

  const toggleRole = (current: string[], role: string) =>
    current.includes(role) ? current.filter((item) => item !== role) : [...current, role];

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await userApi.adminCreateUser({
        email: form.email,
        username: form.username,
        mobile: form.mobile,
        password: form.password,
        role: form.roles[0] || 'User',
        roles: form.roles,
        courseOwnerRevenuePercent: hasCourseOwner(form.roles) && form.courseOwnerRevenuePercent !== '' ? Number(form.courseOwnerRevenuePercent) : null,
      });
      setForm({ email: '', username: '', mobile: '', password: '', roles: ['User'], courseOwnerRevenuePercent: '' });
      setShowCreate(false);
      setMessage('User created.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not create the user.');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        username: edit.username,
        mobile: edit.mobile,
        roles: edit.roles,
        isActive: edit.isActive,
        isEmailVerified: edit.isEmailVerified,
      };
      if (edit.password) payload.password = edit.password;
      if (hasCourseOwner(edit.roles) && edit.courseOwnerRevenuePercent !== '') payload.courseOwnerRevenuePercent = Number(edit.courseOwnerRevenuePercent);
      await userApi.adminUpdateUser(editingId, payload);
      setEditingId(null);
      setMessage('User updated.');
      load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not update the user.');
    } finally {
      setSaving(false);
    }
  };

  const selectClassName = 'mt-1 w-full rounded-md border-gray-300';
  const roleBoxes = (selected: string[], onChange: (next: string[]) => void) => (
    <div className="grid sm:grid-cols-2 gap-2">
      {roles.map((role) => (
        <label key={role} className="text-sm flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected.includes(role)}
            onChange={() => onChange(toggleRole(selected, role))}
          />
          {role}
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-gray-600 max-w-2xl">Users, profiles, and multiple roles. Owner assigns every role a person should hold.</p>
        <Button type="button" onClick={() => setShowCreate(true)}>
          <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New user</span>
        </Button>
      </div>
      {error && !editingId && !showCreate && <p className="text-error-600 text-sm">{error}</p>}
      {message && !editingId && !showCreate && <ActionStatus className="" message={message} />}

      <div className="rounded-xl border bg-white p-4 space-y-4">
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
          <Input label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, or mobile" />
          <label className="text-sm text-gray-700">
            Active
            <select className={selectClassName} value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Verified
            <select className={selectClassName} value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="verified">Email verified</option>
              <option value="unverified">Email unverified</option>
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Identity provider
            <select className={selectClassName} value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="Email / password">Email / password</option>
              <option value="Google">Google</option>
              <option value="LinkedIn">LinkedIn</option>
            </select>
          </label>
        </div>
        <div>
          <p className="text-sm text-gray-700 mb-2">Roles (match any selected)</p>
          <div className="flex flex-wrap gap-3">
            {roles.map((role) => (
              <label key={role} className="text-sm flex items-center gap-2">
                <input type="checkbox" checked={roleFilters.includes(role)} onChange={() => toggleRoleFilter(role)} />
                {role}
              </label>
            ))}
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={includeNoRole} onChange={(e) => setIncludeNoRole(e.target.checked)} />
              No role
            </label>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-500">{filteredUsers.length} of {users.length} users</p>
          {appliedFilters.length > 0 && (
            <button type="button" className="text-sm font-semibold text-primary-700" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
        <AppliedFilters items={appliedFilters} onClear={clearFilters} />
      </div>

      <div className="space-y-2">
        {filteredUsers.map((item) => {
          const itemRoles = item.roles?.length ? item.roles : (item.role ? [item.role] : []);
          return (
            <div key={item.id} className="rounded-xl border bg-white p-4">
              {editingId === item.id ? (
                <div className="grid md:grid-cols-2 gap-3">
                  <Input label="Username" value={edit.username} onChange={(e) => setEdit({ ...edit, username: e.target.value })} />
                  <Input label="Mobile" value={edit.mobile} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} />
                  <Input label="New password (optional)" type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} />
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-700 mb-2">Roles</p>
                    {roleBoxes(edit.roles, (roles) => setEdit({ ...edit, roles }))}
                  </div>
                  {hasCourseOwner(edit.roles) && (
                    <Input label="Course owner revenue %" type="number" value={edit.courseOwnerRevenuePercent} onChange={(e) => setEdit({ ...edit, courseOwnerRevenuePercent: e.target.value })} />
                  )}
                  <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={edit.isActive} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> Active</label>
                  <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={edit.isEmailVerified} onChange={(e) => setEdit({ ...edit, isEmailVerified: e.target.checked })} /> Email verified</label>
                  <div>
                    <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={saveEdit} disabled={saving}>Save</Button>
                    <Button type="button" size="sm" variant="outline" onClick={async () => {
                      const ok = await confirmAction({
                        title: 'Discard user changes?',
                        description: 'Unsaved edits to this user will be lost.',
                        confirmLabel: 'Discard',
                        cancelLabel: 'Keep editing',
                        variant: 'danger',
                      });
                      if (!ok) return;
                      setEditingId(null);
                    }}>Cancel</Button>
                    </div>
                    <ActionStatus message={editingId === item.id ? message : null} error={editingId === item.id ? error : null} />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-900">{item.username || item.email} <span className="text-sm font-normal text-gray-500">{item.email}</span></p>
                    <p className="text-sm text-gray-600">
                      {itemRoles.join(', ') || 'No role'} · {identityLabel(item)} · {item.isActive ? 'Active' : 'Inactive'} · {item.isEmailVerified ? 'Email verified' : 'Email unverified'}
                      {item.courseOwnerRevenuePercent != null ? ` · ${item.courseOwnerRevenuePercent}% course share` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => {
                      setEditingId(item.id);
                      setEdit({ username: item.username || '', mobile: item.mobile || '', roles: itemRoles.length ? itemRoles : ['User'], isActive: item.isActive, isEmailVerified: item.isEmailVerified, password: '', courseOwnerRevenuePercent: item.courseOwnerRevenuePercent == null ? '' : String(item.courseOwnerRevenuePercent) });
                    }}>Edit</Button>
                    {item.isActive ? (
                      <Button type="button" size="sm" variant="outline" onClick={async () => {
                        const ok = await confirmAction({
                          title: 'Deactivate this user?',
                          description: `${item.username || item.email} will not be able to sign in until you reactivate the account.`,
                          confirmLabel: 'Deactivate',
                          cancelLabel: 'Keep active',
                          variant: 'danger',
                        });
                        if (!ok) return;
                        userApi.adminDeactivateUser(item.id).then(load);
                      }}>Deactivate</Button>
                    ) : (
                      <Button type="button" size="sm" variant="outline" onClick={() => userApi.adminUpdateUser(item.id, { isActive: true }).then(load)}>Reactivate</Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filteredUsers.length === 0 && (
          <p className="text-sm text-gray-500">No users match the current search and filters.</p>
        )}
      </div>

      <Modal open={showCreate} title="Create user" onClose={() => setShowCreate(false)}>
          <form className="grid md:grid-cols-2 gap-3" onSubmit={create}>
            <Input label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <Input label="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <Input label="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            <Input label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <div className="md:col-span-2">
              <p className="text-sm text-gray-700 mb-2">Roles</p>
              {roleBoxes(form.roles, (roles) => setForm({ ...form, roles }))}
            </div>
            {hasCourseOwner(form.roles) && (
              <Input label="Course owner revenue % of fees collected" type="number" value={form.courseOwnerRevenuePercent} onChange={(e) => setForm({ ...form, courseOwnerRevenuePercent: e.target.value })} />
            )}
            <div>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Create'}</Button>
              <ActionStatus message={message} error={error} />
            </div>
          </form>
      </Modal>
    </div>
  );
};

export default UsersAdminPage;
